/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RouterProxy } from '../router-proxy';
import { InstallPages } from '@/services/install-pages/install-pages';
import { RouterService } from '@telecom/shared/services/router';
import { CommandRegistry, Emitter } from '@patsnap/tahiti-core';
import { ROUTER_COMMAND_PREFIX } from '../constants';
import type { RouteRecordRaw } from 'vue-router';
import type { CustomQueryParams, CustomRouteLocation, CustomRouteLocationRaw } from '../types';
import { OpenNewTask } from '@/services/open-new-task/open-new-task';
import { isInIframe } from '@telecom/shared/utils/in-iframe';
import { sendRouterChangeMessage } from '@/utils/send-parent-message';

@injectable()
export class RouterProxyImpl implements RouterProxy {
    private _onRouteChangeEmitter = new Emitter<CustomRouteLocation>();
    readonly onRouteChange = this._onRouteChangeEmitter.event;

    constructor(
        @inject(InstallPages)
        private readonly _installPages: InstallPages,
        @inject(RouterService)
        private readonly _routerService: RouterService,
        @inject(CommandRegistry)
        private readonly _commandRegistry: CommandRegistry,
        @inject(OpenNewTask)
        private readonly _openNewTask: OpenNewTask,
    ) {
        _routerService.afterEach(() => {
            this._onRouteChangeEmitter.fire(this.currentRoute);
        });
    }

    public get currentRoute(): CustomRouteLocation {
        const route = this._routerService.currentRoute.value;

        return {
            ...route,
            query: route.query as CustomQueryParams,
        };
    }

    get win() {
        return isInIframe() ? window.parent : window;
    }

    private _registerCommand(name: string) {
        const commandId = `${ROUTER_COMMAND_PREFIX}${name}`;

        this._commandRegistry.registerCommand({
            id: commandId,
        }, {
            execute: (query?: CustomQueryParams) => {
                // 需要处理在 iframe 内路由跳转的命令
                const needHandelInIframeCommands = ['home'].map(name => `${ROUTER_COMMAND_PREFIX}${name}`);
                if (needHandelInIframeCommands.includes(commandId) && isInIframe()) {
                    sendRouterChangeMessage({ path: `/ip/${name}`, query });
                } else {
                    return this.push({ name, query });
                }
            },
        });


        this._commandRegistry.registerCommand({
            id: `${commandId}:_blank`,
        }, {
            execute: (query?: CustomQueryParams) => {
                if (isInIframe()) {
                    const queryParams = new URLSearchParams(query as Record<string, string>);
                    const queryString = queryParams.toString();
                    const url = `/ip/${name}${queryString ? `?${queryString}` : ''}`;
                    this.win.open(url, '_blank');
                } else {
                    const { href } = this._routerService.resolve({ name, query });
                    this.win.open(href, '_blank');
                }
            },
        });

        const newTabCommandId = `${commandId}:new-tab`;

        this._commandRegistry.registerCommand({
            id: newTabCommandId,
        }, {
            execute: (taskId?: string) => {
                this._openNewTask.open(name, taskId);
            },
        });
    }

    private _installCommand() {
        const manifests = this._installPages.getManifests();

        manifests.forEach(manifest => {
            this._registerCommand(manifest.name);
        });
    }

    private _installRouter() {
        const manifests = this._installPages.getManifests();
        const routes: RouteRecordRaw[] = [];

        manifests.forEach((manifest) => {
            routes.push({
                path: manifest.exclusiveRoutePath,
                name: manifest.name,
                component: manifest.getComponent,
                meta: {
                    type: manifest.type,
                },
            });
        });

        const entry = this._installPages.getEntryManifest();
        const defaultRoute = routes.find(route => route.name === entry.name) || routes[0];

        return {
            routes,
            defaultRoute,
        };
    }


    public install() {
        this._installCommand();
        return this._installRouter();
    }

    public async push(to: CustomRouteLocationRaw) {
        await this._routerService.push(to);
    }
}
